/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

/**
 * VFX Shots API Client
 *
 * Handles all VFX shot operations including:
 * - CRUD operations for VFX shots
 * - Shot assignment to artists
 * - Status management
 * - Detection from timeline commits
 * - Notifications
 */

import type {
  VfxShot,
  CreateVfxShotInput,
  UpdateVfxShotInput,
  VfxShotAssignRequest,
  VfxShotStatusUpdateRequest,
  VfxShotNotification,
  VfxShotDetectionResult,
} from '@/types/database'
import { supabase } from '../supabase'

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

/**
 * Get auth token from Supabase
 */
async function getAuthToken(): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session?.access_token) {
    throw new Error('No authentication session found')
  }

  return session.access_token
}

/**
 * Make an authenticated request to the backend
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken()

  const response = await fetch(`${BACKEND_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const errorText = await response.text()
    let errorMessage: string

    try {
      const errorJson = JSON.parse(errorText)
      errorMessage = errorJson.detail || errorJson.message || 'Request failed'
    } catch {
      errorMessage = errorText || `HTTP ${response.status}: ${response.statusText}`
    }

    throw new Error(errorMessage)
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

// =============================================
// VFX SHOT CRUD OPERATIONS
// =============================================

/**
 * Create a new VFX shot
 */
export async function createVfxShot(
  projectId: string,
  shotData: CreateVfxShotInput
): Promise<VfxShot> {
  return apiRequest<VfxShot>(`/api/projects/${projectId}/vfx-shots`, {
    method: 'POST',
    body: JSON.stringify(shotData),
  })
}

/**
 * List VFX shots for a project
 */
export async function listVfxShots(
  projectId: string,
  options?: {
    status?: string
    assigned_user_id?: string
    limit?: number
    offset?: number
  }
): Promise<{ shots: VfxShot[]; total: number; project_id: string }> {
  const params = new URLSearchParams()

  if (options?.status) params.append('status_filter', options.status)
  if (options?.assigned_user_id)
    params.append('assigned_user_id', options.assigned_user_id)
  if (options?.limit) params.append('limit', options.limit.toString())
  if (options?.offset) params.append('offset', options.offset.toString())

  const queryString = params.toString()
  const url = `/api/projects/${projectId}/vfx-shots${queryString ? `?${queryString}` : ''}`

  return apiRequest(url)
}

/**
 * Get a specific VFX shot by ID
 */
export async function getVfxShot(shotId: string): Promise<VfxShot> {
  return apiRequest(`/api/vfx-shots/${shotId}`)
}

/**
 * Update a VFX shot
 */
export async function updateVfxShot(
  shotId: string,
  updates: UpdateVfxShotInput
): Promise<VfxShot> {
  return apiRequest(`/api/vfx-shots/${shotId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })
}

/**
 * Delete a VFX shot
 */
export async function deleteVfxShot(shotId: string): Promise<void> {
  return apiRequest(`/api/vfx-shots/${shotId}`, {
    method: 'DELETE',
  })
}

// =============================================
// VFX SHOT ASSIGNMENT & STATUS
// =============================================

/**
 * Assign multiple VFX shots to an artist
 */
export async function assignVfxShots(
  projectId: string,
  assignRequest: VfxShotAssignRequest
): Promise<VfxShot[]> {
  return apiRequest(`/api/projects/${projectId}/vfx-shots/assign`, {
    method: 'POST',
    body: JSON.stringify(assignRequest),
  })
}

/**
 * Update VFX shot status
 */
export async function updateVfxShotStatus(
  shotId: string,
  statusUpdate: VfxShotStatusUpdateRequest
): Promise<VfxShot> {
  return apiRequest(`/api/vfx-shots/${shotId}/status`, {
    method: 'PATCH',
    body: JSON.stringify(statusUpdate),
  })
}

// =============================================
// VFX SHOT DETECTION
// =============================================

/**
 * Detect VFX shots from a timeline commit
 */
export async function detectVfxShotsFromCommit(
  projectId: string,
  commitId: string,
  options?: {
    vfx_marker_color?: string
    vfx_tag_name?: string
  }
): Promise<VfxShotDetectionResult> {
  const params = new URLSearchParams({ commit_id: commitId })

  if (options?.vfx_marker_color)
    params.append('vfx_marker_color', options.vfx_marker_color)
  if (options?.vfx_tag_name) params.append('vfx_tag_name', options.vfx_tag_name)

  return apiRequest(`/api/projects/${projectId}/vfx-shots/detect?${params.toString()}`)
}

// =============================================
// NOTIFICATIONS
// =============================================

/**
 * Get VFX notifications for a user
 */
export async function getUserVfxNotifications(
  userId: string,
  options?: {
    unread_only?: boolean
    limit?: number
  }
): Promise<VfxShotNotification[]> {
  const params = new URLSearchParams()

  if (options?.unread_only)
    params.append('unread_only', options.unread_only.toString())
  if (options?.limit) params.append('limit', options.limit.toString())

  const queryString = params.toString()
  const url = `/api/users/${userId}/vfx-notifications${queryString ? `?${queryString}` : ''}`

  return apiRequest(url)
}

/**
 * Mark a notification as read
 */
export async function markNotificationAsRead(
  notificationId: string
): Promise<VfxShotNotification> {
  return apiRequest(`/api/vfx-notifications/${notificationId}/read`, {
    method: 'PATCH',
  })
}

/**
 * Check backend health
 */
export async function checkBackendHealth(): Promise<{
  status: string
  features: Record<string, boolean>
}> {
  const response = await fetch(`${BACKEND_URL}/health`)
  if (!response.ok) {
    throw new Error('Backend health check failed')
  }
  return response.json()
}
